import * as Config from '@/Config';
import * as Event from '@/App/Event';
import ChalkySticks from '@chalkysticks/sdk';
import Store from '@/Store';

/**
 * Provider-agnostic subscription service that wraps the SDK's Subscription utility.
 *
 * This service acts as a thin integration layer between your app and the SDK's
 * subscription system. The SDK uses a strategy pattern (e.g., RevenueCat, Stripe)
 * so this service remains agnostic to the underlying provider.
 *
 * Most subscription operations should be performed directly through the SDK:
 *
 *   - ChalkySticks.Utility.Subscription.hasActiveSubscription()
 *   - ChalkySticks.Utility.Subscription.hasEntitlement('premium')
 *   - ChalkySticks.Utility.Subscription.getCustomerInfo()
 *   - ChalkySticks.Utility.Subscription.getOfferings()
 *   - ChalkySticks.Utility.Subscription.restorePurchases()
 *   - ChalkySticks.Utility.Subscription.canMakePayments()
 *   - ChalkySticks.Utility.Subscription.clearCache()
 *
 * This service provides:
 *
 *   - App-specific initialization with Config values
 *   - Vuex Store integration
 *   - User data synchronization
 *   - App-level event bridging
 *   - Convenience methods (e.g., purchaseProduct by identifier)
 *
 * @example Basic Initialization (in App.vue) ✅
 *
 *   import * as Service from '@/App/Service';
 *
 *   // In your app setup
 *   public async setupSubscription(): Promise<void> {
 *       await Service.Subscription.initialize();
 *   }
 *
 * @example Checking Subscription Status (in components)
 *
 *   import ChalkySticks from '@chalkysticks/sdk';
 *
 *   // Check if user has any active subscription
 *   const hasSubscription = await ChalkySticks.Utility.Subscription.hasActiveSubscription();
 *
 *   // Check for specific entitlement
 *   const isPremium = await ChalkySticks.Utility.Subscription.hasEntitlement('premium');
 *
 *   // Get detailed customer info
 *   const customerInfo = await ChalkySticks.Utility.Subscription.getCustomerInfo();
 *   console.log('Active entitlements:', customerInfo?.entitlements.active);
 *
 * @example Fetching and Displaying Offerings (in subscription page)
 *
 *   import ChalkySticks from '@chalkysticks/sdk';
 *
 *   // Get available subscription packages
 *   const offerings = await ChalkySticks.Utility.Subscription.getOfferings();
 *
 *   // Display packages to user
 *   offerings?.availablePackages.forEach(pkg => {
 *       console.log(`${pkg.product.title}: ${pkg.product.priceString}`);
 *   });
 *
 * @example Making a Purchase (via Vuex store)
 *
 *   // In a component - use the store action for state management
 *   const success = await this.$store.dispatch('subscription/purchasePackage', selectedPackage);
 *
 *   if (success) {
 *       this.$router.push({ name: 'PageSubscriptionSuccess' });
 *   }
 *
 * @example Purchasing by Product ID (convenience method)
 *
 *   import * as Service from '@/App/Service';
 *
 *   // If you know the product identifier
 *   try {
 *       const result = await Service.Subscription.purchaseProduct('com.chalkysticks.premium.monthly');
 *       console.log('Purchase successful!', result);
 *   } catch (error) {
 *       if (error.code === 'PURCHASE_CANCELLED_ERROR') {
 *           console.log('User cancelled');
 *       }
 *   }
 *
 * @example Restoring Purchases
 *
 *   import ChalkySticks from '@chalkysticks/sdk';
 *
 *   try {
 *       const customerInfo = await ChalkySticks.Utility.Subscription.restorePurchases();
 *       const hasActive = Object.keys(customerInfo?.entitlements.active || {}).length > 0;
 *       console.log(hasActive ? 'Purchases restored!' : 'No purchases found');
 *   } catch (error) {
 *       console.error('Restore failed:', error);
 *   }
 *
 * @example Listening for Subscription Events
 *
 *   import ChalkySticks from '@chalkysticks/sdk';
 *   import * as Event from '@/App/Event';
 *
 *   // SDK-level events (provider-agnostic)
 *   ChalkySticks.Core.Event.Bus.on('subscription:purchase:success', (event) => {
 *       console.log('Purchase completed:', event.data);
 *   });
 *
 *   ChalkySticks.Core.Event.Bus.on('subscription:status:updated', (event) => {
 *       console.log('Status changed:', event.data);
 *   });
 *
 *   // App-level events (for backward compatibility)
 *   ChalkySticks.Core.Event.Bus.on(Event.Subscription.PurchaseSuccess, (event) => {
 *       // Handle purchase success
 *   });
 *
 * @example Using with Vuex Store
 *
 *   // The store module provides reactive state and actions
 *   computed: {
 *       isSubscribed(): boolean {
 *           return this.$store.getters['subscription/hasActiveSubscription'];
 *       },
 *       offerings(): any {
 *           return this.$store.getters['subscription/availableOfferings'];
 *       }
 *   }
 *
 *   // Dispatch actions
 *   await this.$store.dispatch('subscription/fetchOfferings');
 *   await this.$store.dispatch('subscription/restorePurchases');
 *
 * @author Matt Kenefick <matt@polymermallard.com>
 * @package Service
 * @project ChalkySticks
 */
class Subscription {
	/**
	 * Whether the subscription service has been initialized
	 *
	 * @type boolean
	 */
	private initialized: boolean = false;

	/**
	 * Initialize the subscription system with app-specific configuration.
	 * Sets up the RevenueCat strategy, configures API keys, and establishes
	 * event listeners for Store integration.
	 *
	 * @return Promise<boolean>
	 */
	public async initialize(): Promise<boolean> {
		// Check if running on native platform
		if (!(await ChalkySticks.Utility.Capacitor.isNativePlatform())) {
			ChalkySticks.Utility.Log.info('Subscription', 'Not available on web platform');
			return false;
		}

		// Prevent multiple initializations
		if (this.initialized) {
			ChalkySticks.Utility.Log.warn('Subscription', 'Already initialized');
			return true;
		}

		try {
			// Create and set the RevenueCat strategy
			const strategy = new ChalkySticks.Core.Strategy.Subscription.RevenueCat();
			ChalkySticks.Utility.Subscription.setStrategy(strategy);

			// Configure with API keys from Config
			const config = {
				apiKeyAndroid: Config.Payment.REVENUECAT_SDK_KEY_ANDROID,
				apiKeyIOS: Config.Payment.REVENUECAT_SDK_KEY_IOS,
				apiKeyTest: Config.Payment.REVENUECAT_SDK_KEY_TEST,
				store: Store,
				useTestKey: !Config.Environment.IS_PRODUCTION,
			};

			// Initialize through SDK
			const success = await ChalkySticks.Utility.Subscription.initialize(config);

			if (!success) {
				ChalkySticks.Utility.Log.error('Subscription', 'Failed to initialize through SDK');
				return false;
			}

			// Set up event listeners for Store integration
			this.setupEventListeners();

			// Mark as initialized
			this.initialized = true;

			// Initialize subscription store module
			await Store.dispatch('subscription/initialize');

			// Sync user data if authenticated
			const user = Store.getters['authentication/user'];

			if (user) {
				await ChalkySticks.Utility.Subscription.login(user.id);
				await this.syncUserData(user);
			}

			ChalkySticks.Utility.Log.info('Subscription', 'Setup complete');
			return true;
		} catch (error) {
			ChalkySticks.Utility.Log.error('Subscription', 'Failed to initialize:', error);
			return false;
		}
	}

	/**
	 * Check if the subscription service has been initialized and is ready.
	 *
	 * @return boolean
	 */
	public isInitialized(): boolean {
		return this.initialized && ChalkySticks.Utility.Subscription.isInitialized();
	}

	/**
	 * Purchase a product by its identifier. This is a convenience method that
	 * finds the package containing the specified product and initiates purchase.
	 *
	 * For most cases, use the Vuex store action instead:
	 *   this.$store.dispatch('subscription/purchasePackage', pkg)
	 *
	 * @param string productIdentifier
	 * @return Promise<ISubscriptionPurchaseResult | null>
	 */
	public async purchaseProduct(productIdentifier: string): Promise<ISubscriptionPurchaseResult | null> {
		const offerings = await ChalkySticks.Utility.Subscription.getOfferings();

		if (!offerings) {
			throw new Error('No offerings available');
		}

		// Find the package containing this product
		for (const pkg of offerings.availablePackages) {
			if (pkg.product.identifier === productIdentifier) {
				return await ChalkySticks.Utility.Subscription.purchasePackage(pkg);
			}
		}

		throw new Error(`Product not found: ${productIdentifier}`);
	}

	/**
	 * Sync user information with the subscription provider.
	 * Sets user attributes for segmentation and analytics.
	 *
	 * @param ChalkySticks.Model.User user
	 * @return Promise<void>
	 */
	public async syncUserData(user: ChalkySticks.Model.User): Promise<void> {
		if (!user) {
			return;
		}

		try {
			// Build attributes object
			const attributes: Record<string, string> = {
				email: user.attributes.email || '',
				name: user.getName() || '',
				registration_date: user.getCreatedAt() || '',
				user_id: user.id,
				username: user.getSlug() || '',
			};

			// Remove empty values
			Object.keys(attributes).forEach((key) => {
				if (!attributes[key]) {
					delete attributes[key];
				}
			});

			await ChalkySticks.Utility.Subscription.setUserAttributes(attributes);
			ChalkySticks.Utility.Log.info('Subscription', 'User data synced');
		} catch (error) {
			ChalkySticks.Utility.Log.error('Subscription', 'Failed to sync user data:', error);
		}
	}

	/**
	 * Set up event listeners to bridge SDK events to app-level events
	 * and handle Store integration.
	 *
	 * @return void
	 */
	private setupEventListeners(): void {
		// Bridge SDK events to app-level events for backward compatibility
		ChalkySticks.Core.Event.Bus.on('subscription:purchase:success', async (event: any) => {
			ChalkySticks.Utility.Log.info('Subscription', 'Purchase successful:', event.data);

			// Update store
			await Store.dispatch('subscription/fetchCustomerInfo', true);
			await Store.dispatch('authentication/updateUser');

			// Dispatch app-level event
			ChalkySticks.Core.Event.Bus.dispatch(Event.Subscription.PurchaseSuccess, event.data);
		});

		ChalkySticks.Core.Event.Bus.on('subscription:purchase:cancelled', (event: any) => {
			ChalkySticks.Utility.Log.info('Subscription', 'Purchase cancelled by user');
			ChalkySticks.Core.Event.Bus.dispatch(Event.Subscription.PurchaseCancelled, event.data);
		});

		ChalkySticks.Core.Event.Bus.on('subscription:purchase:error', (event: any) => {
			ChalkySticks.Utility.Log.error('Subscription', 'Purchase error:', event.data);
			ChalkySticks.Core.Event.Bus.dispatch(Event.Subscription.PurchaseError, event.data);
		});

		ChalkySticks.Core.Event.Bus.on('subscription:restore:success', async (event: any) => {
			ChalkySticks.Utility.Log.info('Subscription', 'Restore successful:', event.data);

			// Update store
			await Store.dispatch('subscription/fetchCustomerInfo', true);
			await Store.dispatch('authentication/updateUser');

			// Dispatch app-level event
			ChalkySticks.Core.Event.Bus.dispatch(Event.Subscription.RestoreSuccess, event.data);
		});

		ChalkySticks.Core.Event.Bus.on('subscription:restore:empty', (event: any) => {
			ChalkySticks.Utility.Log.info('Subscription', 'No purchases to restore');
			ChalkySticks.Core.Event.Bus.dispatch(Event.Subscription.RestoreEmpty, event.data);
		});

		ChalkySticks.Core.Event.Bus.on('subscription:restore:error', (event: any) => {
			ChalkySticks.Utility.Log.error('Subscription', 'Restore error:', event.data);
			ChalkySticks.Core.Event.Bus.dispatch(Event.Subscription.RestoreError, event.data);
		});

		ChalkySticks.Core.Event.Bus.on('subscription:status:updated', (event: any) => {
			ChalkySticks.Utility.Log.info('Subscription', 'Status updated:', event.data);
			ChalkySticks.Core.Event.Bus.dispatch(Event.Subscription.StatusUpdated, event.data);
		});

		// Handle authentication events
		ChalkySticks.Core.Event.Bus.on(Event.Authentication.Login, async (event: any) => {
			const user = event.data?.user;

			if (user) {
				await ChalkySticks.Utility.Subscription.login(user.id);
				await this.syncUserData(user);
			}
		});

		ChalkySticks.Core.Event.Bus.on(Event.Authentication.Logout, async () => {
			await ChalkySticks.Utility.Subscription.logout();
			await Store.dispatch('subscription/logout');
		});

		// Refresh subscription status on app resume
		ChalkySticks.Core.Event.Bus.on(Event.App.Resume, async () => {
			await Store.dispatch('subscription/refreshStatus');
		});

		ChalkySticks.Utility.Log.info('Subscription', 'Event listeners configured');
	}
}

// Export singleton instance
export default new Subscription();
